/**
 * Merchant bulk RMA action HTTP client: start Mark as Done, list new reports, mark viewed.
 * Start failures are swallowed as `{ started: false }` so the global HTTP error modal remains
 * the only UI; callers use `started` to decide whether to refresh the new-report notice.
 */
// eslint-disable-next-line
import { injectable } from "inversify";
import { rootContainer, TYPE } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import {
    IBulkNewReport,
    IBulkRequestReturnOutcome,
    BULK_RMA_ACTION,
    IStartBulkActionPayload,
    IStartBulkActionResponse,
    IStartBulkActionResult,
} from "./bulk-rma-action-types";

@injectable()
class BulkRmaActionService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    public async startAction(
        action: BULK_RMA_ACTION,
        payload: IStartBulkActionPayload,
    ): Promise<IStartBulkActionResult> {
        try {
            const res = await this.httpService.post<IApiResponse<IStartBulkActionResponse>>(
                `/rms-api/manage-returns/bulk-actions/${action}`,
                payload,
            );
            return { started: true, response: res.payload };
        } catch {
            return { started: false };
        }
    }

    public async fetchNewReports(): Promise<IBulkNewReport | null> {
        const res = await this.httpService.get<IApiResponse<{ request: IBulkNewReport | null }>>(
            "/rms-api/manage-returns/bulk-actions/new-reports",
        );
        return res.payload.request ?? null;
    }

    public async markRequestViewed(requestId: number): Promise<void> {
        await this.httpService.post<IApiResponse<{ request_id: number }>>(
            "/rms-api/manage-returns/bulk-actions/view",
            { request_id: requestId },
        );
    }

    public async fetchRequestReturns(requestId: number): Promise<IBulkRequestReturnOutcome[]> {
        const res = await this.httpService.get<IApiResponse<{ returns: IBulkRequestReturnOutcome[] }>>(
            `/rms-api/manage-returns/bulk-actions/${requestId}/returns`,
        );
        return res.payload.returns;
    }
}

export default BulkRmaActionService;
